// Lightweight pathname<->View routing — no router lib, mirrors umbrage-client-pages'
// portal/web/src/routes.ts pattern. The web server (infra track) must 200 any
// extensionless path to index.html (SPA fallback) so deep links work; /auth/callback is
// intercepted before the SPA renders (see main.tsx).
//
// SHARE-5: the recordings view carries optional `folderId`/`tag` filter state, encoded in
// the query string (`/?folder=<id>&tag=<name>`) rather than as separate route segments —
// this is the one state channel both the rail (AppShell, rendered by Shell) and the
// dropdowns (RecordingsList) can reach, and it makes filters shareable/back-button-able for
// free. Single-select only (matches the pre-SHARE-5 dropdown semantics); `status` and the
// search box stay local to RecordingsList, not URL-addressable.

export type View =
  | { name: 'recordings'; folderId?: string | undefined; tag?: string | undefined }
  | { name: 'recording'; id: string }
  | { name: 'record' }
  | { name: 'folders' }
  | { name: 'tags' }
  | { name: 'vocabulary' }
  | { name: 'shared' }
  | { name: 'admin' }
  // Otter history import (docs/specs/otter-import-1.md). `/import/otter/callback` (the OAuth
  // redirect target) parses to this same view; the screen reads the ?code&state itself.
  | { name: 'import' }
  // ONDEMAND-1: auto-transcription settings (nav entry only when the feature is enabled).
  | { name: 'transcription' }
  // VOICE-1: the user's own voice print (nav entry only when the feature is enabled).
  | { name: 'voice-print' };

export function pathOf(view: View): string {
  switch (view.name) {
    case 'recordings': {
      const params = new URLSearchParams();
      if (view.folderId) params.set('folder', view.folderId);
      if (view.tag) params.set('tag', view.tag);
      const qs = params.toString();
      return qs ? `/?${qs}` : '/';
    }
    case 'recording':
      return `/recordings/${encodeURIComponent(view.id)}`;
    case 'record':
      return '/record';
    case 'folders':
      return '/folders';
    case 'tags':
      return '/tags';
    case 'vocabulary':
      return '/vocabulary';
    case 'shared':
      return '/shared';
    case 'admin':
      return '/admin';
    case 'import':
      return '/import';
    case 'transcription':
      return '/transcription';
    case 'voice-print':
      return '/voice-print';
  }
}

/** Builds the `recordings` view from a query string, reading `folder`/`tag` — unknown
 * params are ignored, and an absent/empty param leaves the corresponding field unset
 * (never explicitly `undefined`, to satisfy `exactOptionalPropertyTypes`). */
function recordingsView(search: string): View {
  const params = new URLSearchParams(search);
  const folderId = params.get('folder') || undefined;
  const tag = params.get('tag') || undefined;
  return {
    name: 'recordings',
    ...(folderId ? { folderId } : {}),
    ...(tag ? { tag } : {}),
  };
}

export function parsePath(pathname: string, search: string): View {
  const segs = pathname
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean);
  const [head, param] = segs;
  switch (head) {
    case undefined:
      return recordingsView(search);
    case 'record':
      return { name: 'record' };
    case 'folders':
      return { name: 'folders' };
    case 'tags':
      return { name: 'tags' };
    case 'vocabulary':
      return { name: 'vocabulary' };
    case 'shared':
      return { name: 'shared' };
    case 'admin':
      return { name: 'admin' };
    case 'import':
      return { name: 'import' };
    case 'transcription':
      return { name: 'transcription' };
    case 'voice-print':
      return { name: 'voice-print' };
    case 'recordings':
      return param ? { name: 'recording', id: decodeURIComponent(param) } : recordingsView(search);
    default:
      return { name: 'recordings' };
  }
}
